import { websocket, AdminMessageType, isSensitiveMessage } from '@playcastdotio/Messaging';
import type { WebsocketMessage, OutboundMessage, AdminMessage } from '@playcastdotio/Messaging';
import { handlePeerMessage } from '../../api/modules/realtime/handlers/peer';
import { handleAuthMessage } from '../../api/modules/realtime/handlers/auth';
import { handleConnectionMessage } from '../../api/modules/realtime/handlers/connection';
import { handleInvitationMessage } from '../../api/modules/realtime/handlers/invitation';
import { handleLobbyMessage } from '../../api/modules/realtime/handlers/lobby';
import { handleOutboundMessage } from '../../api/modules/realtime/handlers/outbound';
import { handleProfileMessage } from '../../api/modules/realtime/handlers/profile';
import { handleMatchmakerMessage } from '../../api/modules/realtime/handlers/matchmaker';
import { handleUserEventsMessage } from '../../api/modules/realtime/handlers/userEvents';
import { handleUserProfileMessage } from '../../api/modules/realtime/handlers/userProfile';
import { handleNotificationInboxMessage } from '../../api/modules/realtime/handlers/notificationInbox';
import { handleVpnMessage } from '../../api/modules/realtime/handlers/vpn';
import { handleHostMessage } from '../../api/modules/realtime/handlers/host';
import { adminPeerHandler, peerCoordinator, sessionCoordinator } from '../../coordinators/init';
import { realtimeMessageRouter } from '../MessageRouter';
import { startStream } from '../../api/modules/peers/host';
import { USE_MESSAGE_ROUTER, USE_PAVILION_COORDINATOR, USE_PLAYJECTOR_COORDINATOR, USE_SESSION_COORDINATOR } from '../../config/featureFlags';
import * as Sentry from '@sentry/react';

/** Existing inbound dispatch, with private coordinator traffic consumed before legacy logging. */
export function dispatchRealtimeMessage(message: WebsocketMessage, socket: WebSocket | null, onConnectionFailure: () => void): void {
  if (isSensitiveMessage(message)) {
    if (USE_MESSAGE_ROUTER && USE_PAVILION_COORDINATOR && USE_PLAYJECTOR_COORDINATOR) realtimeMessageRouter.route(message);
    return;
  }
  try {
    if (websocket.isConnectionMessage(message)) {
      handleConnectionMessage(message, socket).catch((error) => {
        console.error('[Realtime] Connection message handler failed:', error);
        Sentry.captureException(error, {
          tags: { handler: 'handleConnectionMessage' },
        });
        onConnectionFailure();
      });
    } else if (websocket.isAuthMessage(message)) {
      handleAuthMessage(message);
    } else if (websocket.isInvitationMessage(message)) {
      handleInvitationMessage(message);
    } else if (websocket.isPeerMessage(message)) {
      handlePeerMessage(message);
    } else if (websocket.isLobbyMessage(message)) {
      handleLobbyMessage(message);
    } else if (websocket.isOutboundMessage(message)) {
      handleOutboundMessage(message as OutboundMessage);
    } else if (websocket.isMatchmakingMessage(message)) {
      handleMatchmakerMessage(message);
    } else if (websocket.isProfileMessage(message)) {
      handleProfileMessage(message);
    } else if (websocket.isUserEventMessage(message)) {
      handleUserEventsMessage(message);
    } else if (websocket.isUserProfileMessage(message)) {
      handleUserProfileMessage(message);
    } else if (websocket.isNotificationInboxMessage(message)) {
      handleNotificationInboxMessage(message);
    } else if (websocket.isVpnMessage(message)) {
      handleVpnMessage(message);
    } else if (websocket.isHostMessage(message)) {
      handleHostMessage(message);
    } else if (websocket.isBeaconMessage(message)) {
      realtimeMessageRouter.route(message);
    } else if (websocket.isProvisioningMessage(message)) {
      realtimeMessageRouter.route(message);
    } else if (websocket.isMacroMessage(message)) {
      console.log('[StatefulMacroSystemStep] 🛬 Routing inbound macro message from realtime-api', {
        type: message.type,
      });
      realtimeMessageRouter.route(message);
    } else if (websocket.isAdminMessage(message)) {
      const adminMsg = message as AdminMessage;
      if (adminMsg.type === AdminMessageType.ADMIN_SESSION_PREPARE) {
        console.log('[Realtime] Received ADMIN_SESSION_PREPARE, starting broadcast peer + admin media');
        // In coordinator mode the broadcast peer is owned by the SessionCoordinator /
        // PeerCoordinator (which also serves the admin ShadowCam stream). Admin-exit
        // teardowns (restart_game, keep_game) flow through SessionCoordinator.stopStream,
        // so the broadcast START must use the same subsystem — otherwise the legacy
        // startStream() leaves stale hostState='streaming' that makes the next prepare
        // bail with "Already streaming!", causing the realtime-api prepare to time out
        // and deny the reconnect with `machine_offline`.
        //
        // Capture Cam03 before startStream: streamStarted warm-up shares the shadowcam
        // serializer and used to gUM Cam03 first (pre-seeded, no cold bind), failing open
        // and racing Admin prepare. Media does not need the broadcast peer.
        if (USE_SESSION_COORDINATOR) {
          // Fail closed: never publish hostPeerId after a failed Cam03 bind. Doing so
          // APPROVEs the admin session into Connecting… with no SDP answer (HOST-FRONTEND-VT).
          void (async () => {
            try {
              await peerCoordinator.prepareAdminMedia();
              console.log('[Realtime] Admin media prepared (Cam03 live)');
            } catch (err) {
              console.error('[Realtime] Failed to prepare admin media — not starting broadcast peer:', err);
              return;
            }
            try {
              await sessionCoordinator.beginAdminPrepareBroadcast();
            } catch (err) {
              console.error('[Realtime] Failed to start admin broadcast peer via SessionCoordinator:', err);
            }
          })();
        } else {
          void peerCoordinator
            .prepareAdminMedia()
            .then(() => {
              startStream();
            })
            .catch((err) => {
              console.error('[Realtime] Failed to prepare admin media — not starting broadcast peer:', err);
            });
        }
      } else if (adminPeerHandler) {
        adminPeerHandler.handleAdminSessionMessage(adminMsg);
      }
    } else {
      console.warn('[Realtime] Unhandled inbound websocket message type:', message.type);
    }
  } catch (e) {
    console.error('[Realtime] Failed to handle message:', {
      error: e instanceof Error ? e.message : String(e),
      stack: e instanceof Error ? e.stack : undefined,
    });
  }
}
